import React, { useCallback, useMemo, useState } from 'react';
import { COMMENT_TYPE, LABEL_COLOUR_HASH } from '../../../global/constants';
import Dropdown from '../../Dropdown/MainDropdown';
import DropdownItem from '../../Dropdown/DropdownItem';
import { useDispatch } from 'react-redux';
import { createNewComment, updateTaskDetails } from '../../../services/taskServices';
import {
  BlankText,
  CheckBox,
  Coloricon,
  ContentDiv,
  Icondiv,
  IconMainDiv,
  LabelIcon,
  RightText,
  SvgIconDiv
} from './styles';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { ItemDiv } from '../../customContextMenu/styles';
import { getRawComment, isEmpty } from '../../../helpers/common';
import { TaskModel } from '../../../mobx/models/task';
import { ProjectModel } from '../../../mobx/models/project';
import { rootStore } from '../../../mobx/rootStore';
import { Label } from '../../../mobx/interfaces/project';
import { nanoid } from 'nanoid';

/**
 * Interface defining the props for the LabelDropdownV2 component
 * @interface Props
 * @property {TaskModel} taskData - Optional task data model
 * @property {ProjectModel} currentProject - Current project model
 * @property {boolean} isCreateTask - Flag indicating if this is for task creation
 * @property {boolean} isFromTaskDetails - Flag indicating if rendered from task details
 * @property {boolean} isContextMenu - Flag indicating if this is a context menu
 */
interface Props {
  taskData?: TaskModel;
  currentProject: ProjectModel;
  isCreateTask?: boolean;
  isFromTaskDetails?: boolean;
  isContextMenu?: boolean;
}

const LabelDropdownV2: React.FC<Props> = (props) => {
  //Props declaration
  const { taskData, isCreateTask, isContextMenu, isFromTaskDetails, currentProject } = props;

  //use selector state variables
  const dispatch = useDispatch();
  const [searchResult, setSearchResult] = useState<any[]>(currentProject.labelsList || []); // Default to labelsList

  //Mobx store variables
  const { taskStore, currentUser } = rootStore;
  const { createTaskInput } = taskStore;
  const labelData = isCreateTask ? createTaskInput?.labels : taskData?.labels;

  /**
   * Handles the click event on a label
   * @param {Label} item - The label item that was clicked
   */
  const onClickLabel = useCallback(
    async (item: Label) => {
      if (isCreateTask) {
        // Handle label selection for create task input
        if (createTaskInput?.hasLabel(item.id)) {
          createTaskInput.removeLabel(item.id);
        } else {
          createTaskInput.addLabel(item.id);
        }
      } else {
        // Handle label selection for task details
        if (!currentUser) return;

        let newComment = {
          id: nanoid(),
          type: COMMENT_TYPE.ADD_LABEL,
          group: item?.name
        };

        if (taskData?.hasLabel(item.id)) {
          taskData?.removeLabel(item.id);
          newComment.type = COMMENT_TYPE.REMOVE_LABEL;
        } else {
          taskData?.addLabel(item.id);
        }

        // Update comment in mobx store
        const updatedComment = getRawComment(newComment, currentUser);
        taskData?.upsertComment(updatedComment);

        // Send API request to update the task details and add a new comment
        const payloadTask = { labels: taskData?.labelsIds, projectId: taskData?.projectId };
        await dispatch(updateTaskDetails(taskData?.id, payloadTask));
        await dispatch(
          createNewComment(taskData?.id, {
            Type: newComment.type,
            Group: item?.name
          })
        );
      }
    },
    [isCreateTask, createTaskInput, currentUser, taskData, dispatch]
  );

  // Renders the list of labels with checkboxes
  const renderLabelList = useMemo(() => {
    const labelData = isCreateTask ? createTaskInput?.labels : taskData?.labels;
    return searchResult.map((item) => {
      // Check if the current label is selected
      const isChecked = labelData?.some((label) => label.id === item.id);

      return (
        <DropdownItem
          key={item?.name}
          label={item?.name}
          isSelected={isChecked}
          onClick={(e) => {
            e?.stopPropagation();
            onClickLabel(item);
          }}>
          <CheckBox
            type='checkbox'
            checked={isChecked}
            onClick={(e) => {
              e.stopPropagation();
              onClickLabel(item);
            }}
          />
          <LabelIcon color={`${LABEL_COLOUR_HASH[item?.color]}`} />
        </DropdownItem>
      );
    });
  }, [searchResult, isCreateTask, createTaskInput?.labels, taskData?.labels, onClickLabel]);

  // Renders the selected labels display
  const renderSelectedLabel = useMemo(() => {
    const labelData = isCreateTask ? createTaskInput?.labels : taskData?.labels;
    return labelData?.map((item) => (
      <Coloricon key={item?.id}>
        <IconMainDiv>
          <LabelIcon color={`${LABEL_COLOUR_HASH[item?.color]}`} />
        </IconMainDiv>
        <RightText>{item?.name}</RightText>
      </Coloricon>
    ));
  }, [createTaskInput?.labels, isCreateTask, taskData?.labels]);

  /**
   * Handles search input changes for filtering labels
   * @param {string} value - Search input value
   */
  const onChangeSearch = useCallback(
    (value: string) => {
      // Filter labels based on search input
      const result =
        currentProject.labelsList?.filter((item: any) => item?.name?.toLowerCase().includes(value?.toLowerCase())) ||
        [];

      // Show all labels if search is empty, otherwise show filtered results
      if (isEmpty(value)) {
        setSearchResult(currentProject.labelsList);
      } else {
        setSearchResult(result);
      }
    },
    [currentProject.labelsList]
  );

  return (
    <Dropdown
      isMinWidth={148}
      onChangeSearch={onChangeSearch}
      content={
        isContextMenu ? (
          <ItemDiv>
            <DropdownItem label='Label' iconName='label-icon' iconSize={16} iconTone='stroke' />
          </ItemDiv>
        ) : labelData && labelData?.length > 0 ? (
          <Icondiv isCreateTask={isCreateTask} isTaskDetail={isFromTaskDetails}>
            <SVGIcon name='label-icon' width='16' height='16' viewBox='0 0 16 16' stroke='var(--text-secondary)' />
            {renderSelectedLabel}
          </Icondiv>
        ) : (
          <SvgIconDiv isCreateTask={isCreateTask}>
            <SVGIcon name='label-icon' width='16' height='16' viewBox='0 0 16 16' />
            <BlankText isCreateTask={isCreateTask}>Add Label</BlankText>
          </SvgIconDiv>
        )
      }
      modalTitle='Labels'
      trigger={isContextMenu ? 'hover' : 'click'}>
      <ContentDiv>{renderLabelList}</ContentDiv>
    </Dropdown>
  );
};

export default LabelDropdownV2;
